import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../core/services/api.service';
import {
  ShoppingPartner,
  ShoppingPartnerCategory,
  ShoppingPartnerPayload,
  SubscriptionPlan,
} from '../../core/models/api.models';

const DEFAULT_TIER_SHARES: Record<string, number> = {
  essential: 40,
  plus: 70,
  black: 100,
};

@Component({
  selector: 'app-shopping-partners',
  standalone: true,
  imports: [ReactiveFormsModule, FormsModule],
  templateUrl: './shopping-partners.component.html',
  styleUrl: './shopping-partners.component.scss',
})
export class ShoppingPartnersComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly fb = inject(FormBuilder);

  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly uploading = signal<string | null>(null);
  readonly error = signal<string | null>(null);
  readonly success = signal<string | null>(null);
  readonly rows = signal<ShoppingPartner[]>([]);
  readonly categories = signal<ShoppingPartnerCategory[]>([]);
  readonly plans = signal<SubscriptionPlan[]>([]);
  readonly page = signal(1);
  readonly total = signal(0);
  readonly limit = 20;
  readonly showForm = signal(false);
  readonly editingId = signal<number | null>(null);

  search = '';
  filterCategoryId = '';
  filterFeatured = '';

  /** Dynamic tier share % keyed by plan_key */
  tierShares: Record<string, number> = {};

  /** Staged files — uploaded to S3 on Save (multipart). */
  pendingFiles: { logo: File | null; banner: File | null; card_image: File | null } = {
    logo: null,
    banner: null,
    card_image: null,
  };
  pendingPreviews: { logo: string | null; banner: string | null; card_image: string | null } = {
    logo: null,
    banner: null,
    card_image: null,
  };

  readonly form = this.fb.nonNullable.group({
    name: ['', Validators.required],
    slug: [''],
    aggregator: [''],
    default_cashback_percent: [4, [Validators.required, Validators.min(0), Validators.max(100)]],
    category_id: ['' as string | number],
    tagline: [''],
    short_description: [''],
    about: [''],
    logo_url: [''],
    banner_url: [''],
    card_image_url: [''],
    external_url: [''],
    availability: ['online'],
    is_active: [true],
    show_on_mobile: [true],
    is_featured: [false],
    is_popular: [false],
    is_recommended: [false],
    sort_order: [0],
    metadata_json: [''],
  });

  ngOnInit(): void {
    this.loadCategories();
    this.loadPlans();
    this.load();
  }

  loadCategories(): void {
    this.api.getShoppingPartnerCategories(1, 100).subscribe({
      next: (data) => this.categories.set(data?.rows || []),
      error: () => this.categories.set([]),
    });
  }

  loadPlans(): void {
    this.api.getSubscriptionPlans().subscribe({
      next: (plans) => {
        const list = (plans || []).slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        this.plans.set(list);
        this.initTierShares();
      },
      error: () => this.plans.set([]),
    });
  }

  private initTierShares(fromPartner?: ShoppingPartner | null): void {
    const next: Record<string, number> = {};
    for (const plan of this.plans()) {
      const key = String(plan.plan_key || '').toLowerCase();
      if (!key) continue;
      const found = fromPartner?.points?.find((p) => p.plan_key === key);
      if (found?.tier_share_percent != null) {
        next[key] = Number(found.tier_share_percent);
      } else {
        next[key] = DEFAULT_TIER_SHARES[key] ?? 0;
      }
    }
    this.tierShares = next;
  }

  load(page = 1): void {
    this.loading.set(true);
    this.error.set(null);
    this.page.set(page);
    const filters: Record<string, string | number | boolean> = {};
    if (this.search.trim()) filters['search'] = this.search.trim();
    if (this.filterCategoryId) filters['category_id'] = this.filterCategoryId;
    if (this.filterFeatured === '1') filters['is_featured'] = true;
    if (this.filterFeatured === '0') filters['is_featured'] = false;

    this.api.getShoppingPartners(page, this.limit, filters).subscribe({
      next: (data) => {
        this.rows.set(data?.rows || []);
        this.total.set(data?.total || 0);
        this.loading.set(false);
      },
      error: (err: { message?: string }) => {
        this.error.set(err?.message || 'Failed to load partners');
        this.loading.set(false);
      },
    });
  }

  searchNow(): void {
    this.load(1);
  }

  private clearPendingFiles(): void {
    (['logo', 'banner', 'card_image'] as const).forEach((k) => {
      if (this.pendingPreviews[k]) URL.revokeObjectURL(this.pendingPreviews[k]!);
      this.pendingFiles[k] = null;
      this.pendingPreviews[k] = null;
    });
  }

  openCreate(): void {
    this.editingId.set(null);
    this.clearPendingFiles();
    this.initTierShares(null);
    this.form.reset({
      name: '',
      slug: '',
      aggregator: '',
      default_cashback_percent: 4,
      category_id: '',
      tagline: '',
      short_description: '',
      about: '',
      logo_url: '',
      banner_url: '',
      card_image_url: '',
      external_url: '',
      availability: 'online',
      is_active: true,
      show_on_mobile: true,
      is_featured: false,
      is_popular: false,
      is_recommended: false,
      sort_order: 0,
      metadata_json: '',
    });
    this.showForm.set(true);
    this.success.set(null);
    this.error.set(null);
  }

  openEdit(row: ShoppingPartner): void {
    this.editingId.set(row.id);
    this.clearPendingFiles();
    this.initTierShares(row);
    this.form.patchValue({
      name: row.name || '',
      slug: row.slug || '',
      aggregator: row.aggregator || '',
      default_cashback_percent: Number(row.default_cashback_percent ?? 0),
      category_id: row.category_id || '',
      tagline: row.tagline || '',
      short_description: row.short_description || '',
      about: row.about || '',
      logo_url: row.logo_url || '',
      banner_url: row.banner_url || '',
      card_image_url: row.card_image_url || '',
      external_url: row.external_url || '',
      availability: row.availability || 'online',
      is_active: row.is_active !== false,
      show_on_mobile: row.show_on_mobile !== false,
      is_featured: !!row.is_featured,
      is_popular: !!row.is_popular,
      is_recommended: !!row.is_recommended,
      sort_order: Number(row.sort_order || 0),
      metadata_json: row.metadata ? JSON.stringify(row.metadata, null, 2) : '',
    });
    this.showForm.set(true);
    this.success.set(null);
    this.error.set(null);
  }

  cancelForm(): void {
    this.showForm.set(false);
    this.editingId.set(null);
    this.clearPendingFiles();
  }

  setTierShare(planKey: string, value: string | number): void {
    const n = parseFloat(String(value));
    this.tierShares = {
      ...this.tierShares,
      [planKey]: Number.isFinite(n) ? n : 0,
    };
  }

  private buildPayload(): ShoppingPartnerPayload | null {
    const v = this.form.getRawValue();
    let metadata: Record<string, unknown> | null = null;
    if (v.metadata_json.trim()) {
      try {
        const parsed = JSON.parse(v.metadata_json);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          metadata = parsed as Record<string, unknown>;
        } else {
          this.error.set('metadata must be a JSON object');
          return null;
        }
      } catch {
        this.error.set('Invalid metadata JSON');
        return null;
      }
    }

    const planList = this.plans();
    if (!planList.length) {
      this.error.set('No subscription plans found — create plans first');
      return null;
    }

    const points = planList.map((plan) => {
      const key = String(plan.plan_key).toLowerCase();
      const share = Number(this.tierShares[key] ?? 0);
      return { plan_key: key, tier_share_percent: share };
    });

    for (const p of points) {
      if (p.tier_share_percent < 0 || p.tier_share_percent > 100) {
        this.error.set(`Tier share for ${p.plan_key} must be 0–100`);
        return null;
      }
    }

    return {
      name: v.name.trim(),
      slug: v.slug.trim() || undefined,
      aggregator: v.aggregator.trim() || null,
      default_cashback_percent: Number(v.default_cashback_percent),
      category_id: v.category_id ? Number(v.category_id) : null,
      tagline: v.tagline.trim() || null,
      short_description: v.short_description.trim() || null,
      about: v.about.trim() || null,
      logo_url: v.logo_url.trim() || null,
      banner_url: v.banner_url.trim() || null,
      card_image_url: v.card_image_url.trim() || null,
      external_url: v.external_url.trim() || null,
      availability: v.availability,
      is_active: !!v.is_active,
      show_on_mobile: !!v.show_on_mobile,
      is_featured: !!v.is_featured,
      is_popular: !!v.is_popular,
      is_recommended: !!v.is_recommended,
      sort_order: Number(v.sort_order || 0),
      metadata,
      points,
    };
  }

  save(): void {
    if (this.form.invalid || this.saving()) {
      this.form.markAllAsTouched();
      return;
    }
    const payload = this.buildPayload();
    if (!payload) return;

    this.saving.set(true);
    this.error.set(null);
    const id = this.editingId();
    const hasFiles = !!(
      this.pendingFiles.logo ||
      this.pendingFiles.banner ||
      this.pendingFiles.card_image
    );

    const req$ = hasFiles
      ? this.api.saveShoppingPartnerMultipart(payload, this.pendingFiles, id)
      : id
        ? this.api.updateShoppingPartner(id, payload)
        : this.api.createShoppingPartner(payload);

    req$.subscribe({
      next: () => {
        this.saving.set(false);
        this.showForm.set(false);
        this.editingId.set(null);
        this.clearPendingFiles();
        this.success.set(id ? 'Partner updated' : 'Partner created');
        this.load(this.page());
      },
      error: (err: { message?: string }) => {
        this.saving.set(false);
        this.error.set(err?.message || 'Failed to save partner');
      },
    });
  }

  remove(row: ShoppingPartner): void {
    if (!confirm(`Delete partner “${row.name}”?`)) return;
    this.saving.set(true);
    this.api.deleteShoppingPartner(row.id).subscribe({
      next: () => {
        this.saving.set(false);
        this.success.set('Partner deleted');
        this.load(this.page());
      },
      error: (err: { message?: string }) => {
        this.saving.set(false);
        this.error.set(err?.message || 'Failed to delete partner');
      },
    });
  }

  onFileSelected(
    event: Event,
    field: 'logo' | 'banner' | 'card_image'
  ): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (this.pendingPreviews[field]) URL.revokeObjectURL(this.pendingPreviews[field]!);
    this.pendingFiles[field] = file;
    this.pendingPreviews[field] = URL.createObjectURL(file);
    this.success.set(`${field} selected — will upload to S3 when you save`);
  }

  uploadNow(event: Event, field: 'logo_url' | 'banner_url' | 'card_image_url'): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    const partnerName = this.form.value.name || 'partner';
    const label = field.replace('_url', '');
    this.uploading.set(field);
    this.error.set(null);
    this.api
      .uploadShoppingPartnerImage(file, {
        name: `${partnerName} ${label}`,
        description: `Shopping partner ${label}`,
      })
      .subscribe({
        next: (res) => {
          this.form.patchValue({ [field]: res.url });
          this.uploading.set(null);
          this.success.set('Image uploaded — full URL saved');
          input.value = '';
        },
        error: (err: { message?: string }) => {
          this.uploading.set(null);
          this.error.set(err?.message || 'Image upload failed');
          input.value = '';
        },
      });
  }

  previewFor(field: 'logo' | 'banner' | 'card_image'): string | null {
    return this.pendingPreviews[field] || (this.form.get(field + '_url')?.value as string) || null;
  }

  pointsLabel(row: ShoppingPartner): string {
    const cashback = row.default_cashback_percent != null ? `${row.default_cashback_percent}%` : '—';
    const pts = row.points || [];
    if (!pts.length) return `${cashback} cashback`;
    const shares = pts
      .map((p) => {
        const share =
          p.tier_share_percent != null ? `${p.tier_share_percent}%` : `${p.points_per_dollar} pts/$`;
        return `${p.plan_key}:${share}`;
      })
      .join(' · ');
    return `${cashback} · ${shares}`;
  }

  totalPages(): number {
    return Math.max(1, Math.ceil(this.total() / this.limit));
  }
}
